// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { AddURLLinkParams, urlLinkSchema } from '@/url-link/api/types';
import { z } from 'zod';

const addFulfillmentURLLinkResponseSchema = z
  .object({
    data: urlLinkSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, ...data }));

type AddFulfillmentURLLinkResponse = z.infer<
  typeof addFulfillmentURLLinkResponseSchema
>;

/**
 * Adds a URL link and associates it with a specific fulfillment order.
 * Calls the backend endpoint POST /v1/fulfillment/{fulfillmentKey}/url-link.
 *
 * @param params - The parameters for creating the fulfillment URL link
 * @returns Promise containing the created URL link details
 */
export async function addFulfillmentURLLink(
  params: AddURLLinkParams
): Promise<AddFulfillmentURLLinkResponse> {
  const apiUrl = `/v1/fulfillment/${params.entityKey}/url-link`;
  const payload = {
    name: params.name,
    url: params.url,
  };

  const response = await api.post(apiUrl, payload);

  return parseResponse(addFulfillmentURLLinkResponseSchema, response.data, {
    schemaName: 'AddFulfillmentURLLink',
    endpoint: apiUrl,
  });
}
